import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, Film, Clock, CheckCircle, Ticket, LayoutGrid, Shield, BadgeCheck, Trash2, Plus, DollarSign, Tag, Play } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, apiError } from "@/lib/api";
import { typeLabel } from "@/lib/constants";

const TABS = [
  { id: "overview", label: "Overview", icon: LayoutGrid },
  { id: "content", label: "Content", icon: Film },
  { id: "creators", label: "Creators", icon: BadgeCheck },
  { id: "users", label: "Users", icon: Users },
  { id: "categories", label: "Categories", icon: LayoutGrid },
  { id: "plans", label: "Plans & Pricing", icon: Tag },
  { id: "vouchers", label: "Vouchers", icon: Ticket },
  { id: "payouts", label: "Payouts", icon: DollarSign },
];

export default function Admin() {
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);

  const loadStats = () => api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  useEffect(() => { loadStats(); }, [tab]);

  return (
    <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-10 py-8">
      <div className="flex items-center gap-2 mb-6">
        <Shield className="w-7 h-7 text-[#E50914]" />
        <h1 className="font-display font-black text-3xl sm:text-4xl">Admin Panel</h1>
      </div>

      <div className="flex gap-2 overflow-x-auto no-scrollbar border-b border-white/10 mb-6">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${tab === t.id ? "border-[#E50914] text-white" : "border-transparent text-white/50 hover:text-white"}`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "overview" && <Overview stats={stats} />}
      {tab === "content" && <ContentTab onChange={loadStats} />}
      {tab === "creators" && <CreatorsTab onChange={loadStats} />}
      {tab === "users" && <UsersTab />}
      {tab === "categories" && <CategoriesTab />}
      {tab === "plans" && <PlansTab />}
      {tab === "vouchers" && <VouchersTab />}
      {tab === "payouts" && <PayoutsTab />}
    </div>
  );
}

function Overview({ stats }) {
  if (!stats) return <Loader />;
  const cards = [
    { icon: Users, label: "Total Users", value: stats.users },
    { icon: BadgeCheck, label: "Creators", value: stats.creators },
    { icon: Clock, label: "Pending Creators", value: stats.pending_creators, alert: stats.pending_creators > 0 },
    { icon: Film, label: "Total Content", value: stats.content },
    { icon: Clock, label: "Pending Content", value: stats.pending_content, alert: stats.pending_content > 0 },
    { icon: CheckCircle, label: "Approved", value: stats.approved_content },
    { icon: Crown, label: "Active Subs", value: stats.active_subscriptions },
    { icon: Ticket, label: "Vouchers", value: stats.vouchers },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="admin-overview">
      {cards.map((c) => (
        <div key={c.label} className={`glass rounded-2xl p-5 ${c.alert ? "border-[#E50914]/40" : ""}`}>
          <c.icon className={`w-5 h-5 mb-2 ${c.alert ? "text-[#E50914]" : "text-[#D4AF37]"}`} />
          <p className="font-display font-black text-3xl">{c.value}</p>
          <p className="text-xs text-white/50 mt-1">{c.label}</p>
        </div>
      ))}
    </div>
  );
}
const Crown = (props) => <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m2 4 3 12h14l3-12-6 7-4-7-4 7z" /></svg>;

function ContentTab({ onChange }) {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("");
  const [review, setReview] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [selected, setSelected] = useState([]);
  const load = () => api.get("/admin/content", { params: filter ? { status: filter } : {} }).then((r) => setItems(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const setStatus = async (id, status, reason) => {
    try { await api.put(`/admin/content/${id}/status`, { status, reason }); toast.success(`Marked ${status}`); load(); onChange?.(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const act = async (status) => {
    if (!review) return;
    await setStatus(review.id, status, status === "REJECTED" ? rejectReason : undefined);
    setReview(null); setRejectReason("");
  };
  const toggleSel = (cid) => setSelected((s) => s.includes(cid) ? s.filter((x) => x !== cid) : [...s, cid]);
  const allPending = (items || []).filter((c) => c.status === "PENDING").map((c) => c.id);
  const toggleAll = () => setSelected((s) => s.length === allPending.length ? [] : allPending);
  const bulk = async (status) => {
    try { await api.put("/admin/content/bulk-status", { ids: selected, status }); toast.success(`${status} ${selected.length} item(s)`); setSelected([]); load(); onChange?.(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const del = async (id) => { try { await api.delete(`/admin/content/${id}`); toast.success("Deleted"); load(); onChange?.(); } catch (e) { toast.error(apiError(e)); } };

  if (!items) return <Loader />;
  return (
    <div>
      <div className="flex gap-2 mb-4">
        {["", "PENDING", "APPROVED", "REJECTED"].map((s) => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`content-filter-${s || "all"}`}
            className={`px-3 py-1.5 rounded-full text-xs font-medium ${filter === s ? "bg-[#E50914]" : "bg-white/5 hover:bg-white/10"}`}>{s || "All"}</button>
        ))}
      </div>
      {allPending.length > 0 && (
        <div className="flex items-center gap-3 mb-4 p-3 rounded-xl bg-white/5 border border-white/10" data-testid="bulk-bar">
          <span className="text-xs text-white/50">{selected.length} selected</span>
          <button onClick={() => bulk("APPROVED")} disabled={!selected.length} data-testid="bulk-approve" className="px-4 py-1.5 rounded-full bg-emerald-500/20 text-emerald-300 text-xs font-semibold disabled:opacity-40">Approve Selected</button>
          <button onClick={() => bulk("REJECTED")} disabled={!selected.length} data-testid="bulk-reject" className="px-4 py-1.5 rounded-full bg-red-500/20 text-red-300 text-xs font-semibold disabled:opacity-40">Reject Selected</button>
        </div>
      )}
      <div className="glass rounded-2xl overflow-hidden overflow-x-auto">
        <table className="w-full text-sm" data-testid="admin-content-table">
          <thead className="bg-white/5 text-white/50 text-xs uppercase"><tr>
            <th className="px-4 py-3 w-8"><input type="checkbox" data-testid="select-all-pending" checked={selected.length === allPending.length && allPending.length > 0} onChange={toggleAll} className="accent-[#E50914]" /></th>
            <th className="text-left px-4 py-3">Title</th><th className="text-left px-4 py-3">Creator</th>
            <th className="text-left px-4 py-3">Type</th><th className="text-left px-4 py-3">Status</th>
            <th className="text-right px-4 py-3">Actions</th></tr></thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.id} className="border-t border-white/5" data-testid={`admin-content-${c.id}`}>
                <td className="px-4 py-3 w-8">
                  {c.status === "PENDING" && <input type="checkbox" data-testid={`select-${c.id}`} checked={selected.includes(c.id)} onChange={() => toggleSel(c.id)} className="accent-[#E50914]" />}
                </td>
                <td className="px-4 py-3"><div className="flex items-center gap-3"><img src={c.thumbnail} alt="" className="w-12 h-8 rounded object-cover" /><span className="truncate max-w-[160px]">{c.title}</span></div></td>
                <td className="px-4 py-3 text-white/60">{c.creator_name}</td>
                <td className="px-4 py-3 text-white/60">{typeLabel(c.content_type)}</td>
                <td className="px-4 py-3"><span className="text-xs">{c.status}</span></td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-2">
                    {c.status === "PENDING" && (
                      <button onClick={() => { setReview(c); setRejectReason(""); }} data-testid={`preview-${c.id}`}
                        className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#E50914]/20 text-[#FF6B75] text-xs hover:bg-[#E50914]/30">
                        <Play className="w-3.5 h-3.5" /> Preview
                      </button>
                    )}
                    {c.status !== "APPROVED" && <button onClick={() => setStatus(c.id, "APPROVED")} data-testid={`approve-${c.id}`} className="px-3 py-1.5 rounded-lg bg-emerald-500/20 text-emerald-300 text-xs hover:bg-emerald-500/30">Approve</button>}
                    {c.status !== "REJECTED" && <button onClick={() => setStatus(c.id, "REJECTED")} data-testid={`reject-${c.id}`} className="px-3 py-1.5 rounded-lg bg-amber-500/20 text-amber-300 text-xs hover:bg-amber-500/30">Reject</button>}
                    <button onClick={() => del(c.id)} data-testid={`admin-delete-${c.id}`} className="p-2 rounded-lg bg-white/5 hover:bg-red-500/20 hover:text-red-300"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!review} onOpenChange={(o) => { if (!o) { setReview(null); setRejectReason(""); } }}>
        <DialogContent className="bg-[#110F18] border-white/10 text-white max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Review — {review?.title}</DialogTitle>
          </DialogHeader>
          {review && (
            <div className="space-y-4" data-testid="review-modal">
              <div className="aspect-video rounded-xl overflow-hidden bg-black border border-white/10">
                {review.video_url ? (
                  <video src={review.video_url} controls className="w-full h-full" data-testid="review-video" poster={review.thumbnail} />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white/40 text-sm">No video attached to this submission</div>
                )}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                <Detail label="Creator" value={review.creator_name} />
                <Detail label="Category" value={review.category} />
                <Detail label="Type" value={typeLabel(review.content_type)} />
                <Detail label="Country" value={review.country} />
                <Detail label="Language" value={review.language} />
                <Detail label="Age Rating" value={review.age_rating} />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-white/40 mb-1">Description</p>
                <p className="text-sm text-white/70 leading-relaxed">{review.description || "—"}</p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 sm:items-end border-t border-white/10 pt-4">
                <div className="flex-1 w-full">
                  <label className="text-xs text-white/50 block mb-1">Rejection reason (optional)</label>
                  <textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} rows={2} data-testid="review-reject-reason"
                    placeholder="Why is this being rejected? (visible in moderation log)"
                    className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm resize-none focus:border-[#E50914]/60 focus:outline-none" />
                </div>
                <div className="flex gap-2">
                  <button onClick={() => act("REJECTED")} data-testid="review-reject-btn"
                    className="px-5 h-10 rounded-lg bg-red-500/20 text-red-300 hover:bg-red-500/30 text-sm font-semibold transition-colors">Reject</button>
                  <button onClick={() => act("APPROVED")} data-testid="review-approve-btn"
                    className="px-5 h-10 rounded-lg bg-emerald-500 text-black font-semibold text-sm hover:bg-emerald-400 transition-colors">Approve & Publish</button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Detail = ({ label, value }) => (
  <div><p className="text-[10px] uppercase tracking-wider text-white/40">{label}</p><p className="text-white/80">{value || "—"}</p></div>
);

function CreatorsTab({ onChange }) {
  const [items, setItems] = useState(null);
  const load = () => api.get("/admin/creators").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const verify = async (id) => { try { const { data } = await api.put(`/admin/creators/${id}/verify`); toast.success(data.verified ? "Verified" : "Unverified"); load(); onChange?.(); } catch (e) { toast.error(apiError(e)); } };
  if (!items) return <Loader />;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {items.map((c) => (
        <div key={c.id} className="glass rounded-2xl p-5 flex items-center gap-4" data-testid={`admin-creator-${c.id}`}>
          {c.avatar ? <img src={c.avatar} alt="" className="w-14 h-14 rounded-full object-cover" /> : <span className="w-14 h-14 rounded-full bg-[#D4AF37]/40 flex items-center justify-center font-bold">{c.display_name?.[0]}</span>}
          <div className="flex-1 min-w-0">
            <p className="font-semibold flex items-center gap-1 truncate">{c.display_name} {c.verified && <BadgeCheck className="w-4 h-4 text-[#E50914]" />}</p>
            <p className="text-xs text-white/50 truncate">{c.follower_count} followers</p>
          </div>
          <button onClick={() => verify(c.id)} data-testid={`verify-${c.id}`}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold ${c.verified ? "bg-white/10" : "bg-[#E50914] hover:bg-[#FF2A3B]"}`}>
            {c.verified ? "Unverify" : "Verify"}
          </button>
        </div>
      ))}
    </div>
  );
}

function UsersTab() {
  const [items, setItems] = useState(null);
  const load = () => api.get("/admin/users").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const setRole = async (id, role) => { try { await api.put(`/admin/users/${id}/role`, { role }); toast.success("Role updated"); load(); } catch (e) { toast.error(apiError(e)); } };
  const del = async (id) => { try { await api.delete(`/admin/users/${id}`); toast.success("User removed"); load(); } catch (e) { toast.error(apiError(e)); } };
  if (!items) return <Loader />;
  return (
    <div className="glass rounded-2xl overflow-hidden overflow-x-auto">
      <table className="w-full text-sm" data-testid="admin-users-table">
        <thead className="bg-white/5 text-white/50 text-xs uppercase"><tr>
          <th className="text-left px-4 py-3">Name</th><th className="text-left px-4 py-3">Email</th>
          <th className="text-left px-4 py-3">Role</th><th className="text-right px-4 py-3">Actions</th></tr></thead>
        <tbody>
          {items.map((u) => (
            <tr key={u.id} className="border-t border-white/5" data-testid={`admin-user-${u.id}`}>
              <td className="px-4 py-3">{u.name}</td>
              <td className="px-4 py-3 text-white/60">{u.email}</td>
              <td className="px-4 py-3">
                <select value={u.role} onChange={(e) => setRole(u.id, e.target.value)} data-testid={`role-select-${u.id}`}
                  className="bg-white/5 border border-white/10 rounded-lg px-2 py-1 text-xs focus:outline-none">
                  {["ADMIN", "CREATOR", "SUBSCRIBER", "FREE_USER"].map((r) => <option key={r} value={r} className="bg-[#1A1724]">{r}</option>)}
                </select>
              </td>
              <td className="px-4 py-3 text-right">
                <button onClick={() => del(u.id)} data-testid={`user-delete-${u.id}`} className="p-2 rounded-lg bg-white/5 hover:bg-red-500/20 hover:text-red-300"><Trash2 className="w-4 h-4" /></button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CategoriesTab() {
  const [items, setItems] = useState(null);
  const [name, setName] = useState("");
  const load = () => api.get("/categories").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const add = async () => { if (!name.trim()) return; try { await api.post("/admin/categories", { name }); setName(""); toast.success("Added"); load(); } catch (e) { toast.error(apiError(e)); } };
  const del = async (id) => { try { await api.delete(`/admin/categories/${id}`); load(); } catch (e) { toast.error(apiError(e)); } };
  if (!items) return <Loader />;
  return (
    <div>
      <div className="flex gap-2 mb-6 max-w-md">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="New category name" data-testid="category-input"
          className="flex-1 h-11 px-4 rounded-lg bg-white/5 border border-white/10 focus:border-[#E50914]/60 focus:outline-none" />
        <button onClick={add} data-testid="category-add" className="px-5 h-11 rounded-lg bg-[#E50914] hover:bg-[#FF2A3B] font-semibold flex items-center gap-1"><Plus className="w-4 h-4" /> Add</button>
      </div>
      <div className="flex flex-wrap gap-2" data-testid="categories-list">
        {items.map((c) => (
          <span key={c.id} className="badge-glass !py-2 !px-3 group">
            {c.name}
            <button onClick={() => del(c.id)} data-testid={`category-delete-${c.id}`} className="ml-1 text-white/40 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
          </span>
        ))}
      </div>
    </div>
  );
}

function VouchersTab() {
  const [items, setItems] = useState(null);
  const [planId, setPlanId] = useState("R79");
  const [count, setCount] = useState(1);
  const load = () => api.get("/admin/vouchers").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const create = async () => { try { await api.post("/admin/vouchers", { plan_id: planId, count: Number(count), duration_days: 30 }); toast.success("Vouchers created"); load(); } catch (e) { toast.error(apiError(e)); } };
  const disable = async (id) => { try { await api.put(`/admin/vouchers/${id}/disable`); load(); } catch (e) { toast.error(apiError(e)); } };
  if (!items) return <Loader />;
  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-6 items-end">
        <div><label className="text-xs text-white/50 block mb-1">Plan</label>
          <select value={planId} onChange={(e) => setPlanId(e.target.value)} data-testid="voucher-plan"
            className="h-11 px-4 rounded-lg bg-white/5 border border-white/10 focus:outline-none">{["R49", "R79", "R99"].map((p) => <option key={p} value={p} className="bg-[#1A1724]">{p} Premium</option>)}</select></div>
        <div><label className="text-xs text-white/50 block mb-1">Count</label>
          <input type="number" value={count} onChange={(e) => setCount(e.target.value)} min={1} max={50} data-testid="voucher-count"
            className="h-11 w-24 px-4 rounded-lg bg-white/5 border border-white/10 focus:outline-none" /></div>
        <button onClick={create} data-testid="voucher-create" className="h-11 px-5 rounded-lg bg-[#E50914] hover:bg-[#FF2A3B] font-semibold flex items-center gap-1"><Plus className="w-4 h-4" /> Generate</button>
      </div>
      <div className="glass rounded-2xl overflow-hidden overflow-x-auto">
        <table className="w-full text-sm" data-testid="admin-vouchers-table">
          <thead className="bg-white/5 text-white/50 text-xs uppercase"><tr>
            <th className="text-left px-4 py-3">Code</th><th className="text-left px-4 py-3">Plan</th>
            <th className="text-left px-4 py-3">Status</th><th className="text-right px-4 py-3">Actions</th></tr></thead>
          <tbody>
            {items.map((v) => (
              <tr key={v.id} className="border-t border-white/5">
                <td className="px-4 py-3 font-mono">{v.code}</td>
                <td className="px-4 py-3">{v.plan_id}</td>
                <td className="px-4 py-3"><span className={`text-xs ${v.status === "active" ? "text-emerald-300" : "text-white/40"}`}>{v.status}</span></td>
                <td className="px-4 py-3 text-right">{v.status === "active" && <button onClick={() => disable(v.id)} data-testid={`voucher-disable-${v.id}`} className="text-xs text-red-300 hover:underline">Disable</button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const Loader = () => <div className="min-h-[30vh] flex items-center justify-center"><div className="w-8 h-8 border-4 border-[#E50914] border-t-transparent rounded-full animate-spin" /></div>;

const PlanField = ({ label, type = "text", value, onChange, testid }) => (
  <div>
    <label className="text-xs text-white/50 block mb-1">{label}</label>
    <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="w-full h-10 px-3 rounded-lg bg-white/5 border border-white/10 focus:border-[#E50914]/60 focus:outline-none text-sm" />
  </div>
);

function PlansTab() {
  const [plans, setPlans] = useState(null);
  const load = () => api.get("/subscription-plans").then((r) =>
    setPlans(r.data.map((p) => ({ ...p, featuresText: (p.features || []).join("\n") }))));
  useEffect(() => { load(); }, []);
  const upd = (i, k, v) => setPlans((ps) => ps.map((p, idx) => (idx === i ? { ...p, [k]: v } : p)));
  const save = async (p) => {
    try {
      await api.put(`/admin/subscription-plans/${p.id}`, {
        name: p.name, amount: Number(p.amount), period: p.period, badge: p.badge,
        description: p.description,
        features: p.featuresText.split("\n").map((f) => f.trim()).filter(Boolean),
      });
      toast.success(`${p.id} plan updated`); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  if (!plans) return <Loader />;
  return (
    <div>
      <p className="text-sm text-white/50 mb-4">Edit plan names, prices and features. Premium prices are charged live via PayPal (billed in USD equivalent).</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="admin-plans">
        {plans.map((p, i) => (
          <div key={p.id} className="glass rounded-2xl p-5 space-y-3" data-testid={`plan-edit-${p.id}`}>
            <div className="flex items-center justify-between">
              <h3 className="font-display font-bold text-lg">{p.id}</h3>
              <span className="text-xs text-white/40">{p.id === "FREE" ? "Free tier" : "Premium tier"}</span>
            </div>
            <PlanField label="Plan name" value={p.name} onChange={(v) => upd(i, "name", v)} testid={`plan-name-${p.id}`} />
            <div className="grid grid-cols-2 gap-3">
              <PlanField label="Price (ZAR)" type="number" value={p.amount} onChange={(v) => upd(i, "amount", v)} testid={`plan-amount-${p.id}`} />
              <PlanField label="Period label" value={p.period} onChange={(v) => upd(i, "period", v)} testid={`plan-period-${p.id}`} />
            </div>
            <PlanField label="Badge" value={p.badge} onChange={(v) => upd(i, "badge", v)} testid={`plan-badge-${p.id}`} />
            <div>
              <label className="text-xs text-white/50 block mb-1">Description</label>
              <textarea value={p.description} onChange={(e) => upd(i, "description", e.target.value)} rows={2} data-testid={`plan-desc-${p.id}`}
                className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm resize-none focus:border-[#E50914]/60 focus:outline-none" />
            </div>
            <div>
              <label className="text-xs text-white/50 block mb-1">Features (one per line)</label>
              <textarea value={p.featuresText} onChange={(e) => upd(i, "featuresText", e.target.value)} rows={4} data-testid={`plan-features-${p.id}`}
                className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm resize-none focus:border-[#E50914]/60 focus:outline-none" />
            </div>
            <button onClick={() => save(p)} data-testid={`plan-save-${p.id}`}
              className="px-5 h-10 rounded-lg bg-[#E50914] hover:bg-[#FF2A3B] font-semibold text-sm transition-colors">Save Changes</button>
          </div>
        ))}
      </div>
    </div>
  );
}

function PayoutsTab() {
  const [items, setItems] = useState(null);
  const load = () => api.get("/admin/payouts").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const setStatus = async (id, status) => { try { await api.put(`/admin/payouts/${id}/status`, { status }); toast.success(`Marked ${status}`); load(); } catch (e) { toast.error(apiError(e)); } };
  if (!items) return <Loader />;
  return (
    <div className="glass rounded-2xl overflow-hidden overflow-x-auto">
      <table className="w-full text-sm" data-testid="admin-payouts-table">
        <thead className="bg-white/5 text-white/50 text-xs uppercase"><tr>
          <th className="text-left px-4 py-3">Creator</th><th className="text-right px-4 py-3">Amount</th>
          <th className="text-left px-4 py-3">Method</th><th className="text-left px-4 py-3">Status</th>
          <th className="text-right px-4 py-3">Actions</th></tr></thead>
        <tbody>
          {items.map((p) => (
            <tr key={p.id} className="border-t border-white/5" data-testid={`admin-payout-${p.id}`}>
              <td className="px-4 py-3">{p.creator_name}</td>
              <td className="px-4 py-3 text-right text-[#D4AF37]">R{p.amount}</td>
              <td className="px-4 py-3 text-white/60">{p.method}</td>
              <td className="px-4 py-3"><span className="text-xs">{p.status}</span></td>
              <td className="px-4 py-3 text-right">
                <div className="flex justify-end gap-2">
                  {p.status !== "paid" && <button onClick={() => setStatus(p.id, "paid")} data-testid={`payout-paid-${p.id}`} className="px-3 py-1.5 rounded-lg bg-emerald-500/20 text-emerald-300 text-xs hover:bg-emerald-500/30">Mark Paid</button>}
                  {p.status !== "rejected" && <button onClick={() => setStatus(p.id, "rejected")} data-testid={`payout-reject-${p.id}`} className="px-3 py-1.5 rounded-lg bg-red-500/20 text-red-300 text-xs hover:bg-red-500/30">Reject</button>}
                </div>
              </td>
            </tr>
          ))}
          {!items.length && <tr><td colSpan={5} className="px-4 py-10 text-center text-white/40">No payout requests yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
